import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@capturum/auth';
import { SettingIndexedDbModel } from '@capturum/complete';
import { AppRoutes } from '@core/enums/routes.enum';
import { Observable } from 'rxjs';
import { filterRequiredPermission } from '@core/operators/filter-required-permission.operator';
import { NgxPermissionsService } from 'ngx-permissions';
import { MenuItem as CompleteMenuItem } from 'primeng/api';
import { finalize, first } from 'rxjs/operators';
import { UserService } from '@features/user/services/user.service';
import { CSSVarNames, ThemeService } from '@capturum/ui/api';
import { updatePrimaryPalette } from '@primeng/themes';
import { Tenant } from '@core/enums/tenant.enum';
import { User } from '../../../../../features/user/models/user.model';
import { WINDOW } from '@core/configs/token.config';

export interface MenuItem extends CompleteMenuItem {
  requiredPermission?: string;
  hidden?: boolean;
}

@Component({
    selector: 'app-user-menu',
    templateUrl: './user-menu.component.html',
    styleUrls: ['./user-menu.component.scss'],
    standalone: false
})
export class UserMenuComponent implements OnInit {
  public menuItems$: Observable<MenuItem[]>;
  public allItems: MenuItem[];
  public user: User;
  public canSwitch = false;
  private window = inject(WINDOW);

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly ngxPermissionsService: NgxPermissionsService,
    private readonly themeService: ThemeService,
    private userService: UserService
  ) {
    this.user = this.authService.getUser() as unknown as User;
  }

  public ngOnInit(): void {
    this.setTheme(this.user?.tenant_name);

    this.userService.canSwitchEnvironment().subscribe((result) => {
      this.canSwitch = result.canSwitchTenant;

      this.allItems = this.getMenuItems();

      this.menuItems$ = this.getFilteredMenuItems(this.allItems);
    });
  }

  public environmentTitle = `${SettingIndexedDbModel.getValue('LeadPortal.tenant_translation_key')}`;

  public getFilteredMenuItems(navItems: MenuItem[]): Observable<MenuItem[]> {
    return this.ngxPermissionsService.permissions$.pipe(first(), filterRequiredPermission(navItems));
  }

  public executeCommand(item: MenuItem): void {
    if (item.command) {
      item.command(item.event);
    } else if (item.routerLink) {
      this.router.navigateByUrl(item.routerLink);
    }
  }

  private getSwitchRedirectRoute(currentRoute: string): string | null {
    const redirectMap: Record<string, string> = {
      '/lead/': '/lead/',
      '/distributor/': '/distributor/',
      '/installer/': '/installer/',
    };

    const matched = Object.keys(redirectMap).find((prefix) => {
      return currentRoute.startsWith(prefix);
    });

    return matched ? redirectMap[matched] : null;
  }

  private getMenuItems(): MenuItem[] {
    return [
      {
        label: 'link.profile',
        icon: 'fas fa-user-circle',
        routerLink: `/${AppRoutes.user}/${AppRoutes.profile}`,
      },
      {
        label: 'config.manage.title',
        icon: 'fas fa-cog',
        routerLink: `/${AppRoutes.config}`,
        requiredPermission: 'module.manage.tenant', // @TODO: Find a permission only admins have
      },
      {
        label: `${SettingIndexedDbModel.getValue('LeadPortal.tenant_switch_translation_key')}`,
        icon: 'fas fa-duotone fa-repeat',
        hidden: !this.canSwitch,
        command: () => {
          this.userService.switchEnvironment().subscribe((data) => {
            const currentRoute = this.router.url;
            const redirectTo = this.getSwitchRedirectRoute(currentRoute);

            this.userService.updateUserData(this.user, data);

            if (redirectTo) {
              this.router.navigate([redirectTo]).then(() => {
                return this.window.location.reload();
              });

              return;
            }

            this.window.location.reload();
          });
        },
      },
      {
        label: 'link.logout',
        icon: 'fas fa-sign-in-alt',
        command: () => {
          this.authService
            .logout()
            .pipe(
              first(),
              finalize(() => {
                this.router.navigate(['/', AppRoutes.auth, AppRoutes.login]);
              })
            )
            .subscribe();
        },
      },
    ];
  }

  private setTheme(tenantName?: string): void {
    if (tenantName === Tenant.AIRCONDITIONING) {
      this.themeService.setProps({
        [CSSVarNames.Primary]: '#0096AA',
        [CSSVarNames.Secondary]: '#006A73',
      });
      updatePrimaryPalette({
        50:  '#f0fafb',
        100: '#b3e0e8',
        200: '#80ccd8',
        300: '#4db8c8',
        400: '#26a7b9',
        500: '#0096AA',
        600: '#008091',
        700: '#006A73',
        800: '#00535A',
        900: '#003C41',
        950: '#002528',
      });
    } else {
      updatePrimaryPalette({
        50:  '#fbf3f5',
        100: '#ebc4ce',
        200: '#dc96a8',
        300: '#cc6781',
        400: '#bd395b',
        500: '#ad0a34',
        600: '#93092c',
        700: '#790724',
        800: '#5f061d',
        900: '#450415',
        950: '#2b030d',
      });
    }
  }
}
